import { Injectable } from '@angular/core';
import { AnalyticsService } from '@lsq-frontend/angular-shared/features/analytics';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { PlatformId } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    atlas,
    supplyChainPlatformAtlas,
    SUPPLY_CHAIN_PLATFORM,
    SUPPLY_CHAIN_PLATFORM_ENROLLMENT_OVERVIEW,
    SUPPLY_CHAIN_PLATFORM_NOT_AUTHORIZED,
    SUPPLY_CHAIN_PLATFORM_SETTINGS
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import {
    GetRelationshipDetailsByExternalIdsRequest,
    GetRelationshipDetailsByExternalIdsResponse,
    GetRelationshipDetailsByExternalIdsService,
    GetRelationshipDetailsRequest,
    GetRelationshipDetailsService,
    GetSellerDetailsRequest,
    GetSellerDetailsService,
    RedactedBankAccount,
    UpdateRelationshipBankAccountRequest,
    UpdateRelationshipBankAccountService
} from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import { AuthorizationType } from '@lsq-frontend/remote-services/enterprise/common';
import { getDynamicRouteLink, mapToKey } from '@lsq-frontend/common-ui';
import { getRouterLinkUrlById } from '@lsq-frontend/common-ui';
import { escapeRegExp } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { UserMetadata } from '../../../../constants/user-metadata.constants';
import { isNotNullOrProtoDefault } from '../../../../helpers/date.helpers';

@Injectable({ providedIn: 'root' })
export class SupplyChainPlatformRelationshipService {
    private _relationshipDetailsSubject = new BehaviorSubject<
        GetRelationshipDetailsByExternalIdsResponse | undefined
    >(undefined);
    private _platformIdSubject = new BehaviorSubject<PlatformId | undefined>(undefined);
    public platformId$ = this._platformIdSubject.asObservable();
    public relationshipDetails$ = this._relationshipDetailsSubject.asObservable();
    public bankAccounts$ = this._relationshipDetailsSubject.pipe(mapToKey('bankAccounts'));
    public buyerName$ = this.relationshipDetails$.pipe(mapToKey('buyerName'));
    public sellerName$ = this.relationshipDetails$.pipe(mapToKey('sellerName'));
    public sellerLegalName$ = this.relationshipDetails$.pipe(mapToKey('sellerLegalName'));
    public discountFeePercent$ = this.relationshipDetails$.pipe(mapToKey('discountFeePercent'));
    public isRelationshipEnrolled$ = this.relationshipDetails$.pipe(
        // Casting to `unknown` because we don't have a standard way of reading dates from the back end.
        // The proto generator currently gives them the type `object`.
        // It's accidental that this isn't a problem in many other places, because most of the date
        // parsing that needs to happen in our app is handled by the data table, which is perfectly
        // fine with parsing an `object`.
        // TODO: Implement a standard way of reading dates from the back end.
        map(({ relationship }) =>
            isNotNullOrProtoDefault((relationship.enrollmentDate as unknown) as string)
        )
    );

    constructor(
        private _userService: UserService,
        private _getRelationshipDetailsService: GetRelationshipDetailsService,
        private _getRelationshipDetailsByExternalIdsService: GetRelationshipDetailsByExternalIdsService,
        private _getSellerDetailsService: GetSellerDetailsService,
        private _analyticsService: AnalyticsService,
        private _updateRelationshipBankAccountService: UpdateRelationshipBankAccountService
    ) {
        this._userService.logout$.subscribe((isLoggedOut) => {
            if (isLoggedOut) {
                this._platformIdSubject.next(undefined);
            }
        });
    }

    public refreshRelationship(): Promise<GetRelationshipDetailsByExternalIdsResponse> {
        return this.platformId$
            .pipe(
                first(),
                switchMap((platformId) => this._loadRelationshipDetailsOrThrow(platformId))
            )
            .toPromise();
    }

    public async acceptTermsAndEnroll(defaultBankAccount: RedactedBankAccount): Promise<void> {
        const { relationship } = await this.relationshipDetails$.pipe(first()).toPromise();

        const request = new UpdateRelationshipBankAccountRequest({
            relationshipKey: relationship.relationshipKey,
            refBankAccountKey: defaultBankAccount.bankAccountKey,
            hasTermsOfService: true
        });
        await this._updateRelationshipBankAccountService.executeOnce(request);

        await this.refreshRelationship();
    }

    public async loadRelationshipAndGetEntrypointUrl(
        platformId: PlatformId,
        options: {
            token?: string;
            sourceUrl?: string;
            isFromRedirect?: boolean;
        }
    ): Promise<string> {
        const { token, sourceUrl, isFromRedirect } = options;
        let routeWithoutQueryString: string;

        if (isFromRedirect) {
            this._analyticsService.triggerSimpleEvent('financing-enrollment-redirect');
        }

        if (platformId === PlatformId.DEFAULT) {
            const user = await this._userService.user$.pipe(first()).toPromise();

            if (user) {
                const sellerDetailsResponse = await this._getSellerDetailsService.executeOnce(
                    new GetSellerDetailsRequest({
                        refSellerKey: user.currentCompany.alternateCompanyKey
                    })
                );

                if (sellerDetailsResponse.platformName) {
                    return getDynamicRouteLink(getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM), {
                        platformId: sellerDetailsResponse.platformName
                    });
                }
            }
        }

        try {
            await this._loadRelationshipDetailsOrThrow(platformId, token);

            const isEnrolled = await this.isRelationshipEnrolled$.pipe(first()).toPromise();

            if (isEnrolled) {
                routeWithoutQueryString = getDynamicRouteLink(
                    getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM_SETTINGS),
                    { platformId }
                );
            } else {
                const OVERVIEW_PATH_REGEX = escapeRegExp(
                    supplyChainPlatformAtlas.SUPPLY_CHAIN_PLATFORM_ENROLLMENT_OVERVIEW.path
                );
                const ENROLL_PATH_REGEX = escapeRegExp(
                    supplyChainPlatformAtlas.SUPPLY_CHAIN_PLATFORM_ENROLLMENT_ENROLL.path
                );
                const NEXT_STEPS_PATH_REGEX = escapeRegExp(
                    supplyChainPlatformAtlas.SUPPLY_CHAIN_PLATFORM_ENROLLMENT_NEXT_STEPS.path
                );
                const NOT_INTERESTED_PATH_REGEX = escapeRegExp(
                    supplyChainPlatformAtlas.SUPPLY_CHAIN_PLATFORM_ENROLLMENT_NOT_INTERESTED.path
                );
                const sourceUrlIsEnrollmentRoute =
                    sourceUrl &&
                    !!sourceUrl.match(
                        new RegExp(
                            `\\/(${OVERVIEW_PATH_REGEX}|${ENROLL_PATH_REGEX}|${NEXT_STEPS_PATH_REGEX}|${NOT_INTERESTED_PATH_REGEX})` +
                                `(\\?|\\/$|\\/?|$)`
                        )
                    );
                if (sourceUrlIsEnrollmentRoute) {
                    routeWithoutQueryString = sourceUrl.split('?')[0];
                } else {
                    routeWithoutQueryString = getDynamicRouteLink(
                        getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM_ENROLLMENT_OVERVIEW),
                        { platformId }
                    );
                }
            }
        } catch (error) {
            console.error(error);
            routeWithoutQueryString = getDynamicRouteLink(
                getRouterLinkUrlById(atlas, SUPPLY_CHAIN_PLATFORM_NOT_AUTHORIZED),
                { platformId }
            );
        }

        return routeWithoutQueryString;
    }

    private async _loadRelationshipDetailsOrThrow(
        platformId: PlatformId,
        token?: string
    ): Promise<GetRelationshipDetailsByExternalIdsResponse> {
        const user = await this._userService.user$.pipe(first()).toPromise();

        if (token) {
            await this._userService.authenticateUser(
                `${platformId.toLowerCase()}@lsq.com`,
                token,
                AuthorizationType.PlatformToken
            );
        } else if (!user) {
            throw new Error('Login failed because no user or token was found.');
        }
        const newUser = await this._userService.user$.pipe(first()).toPromise();

        const metadata = newUser.currentCompany.metadata;
        let relationshipDetails: GetRelationshipDetailsByExternalIdsResponse;
        if (metadata && metadata[UserMetadata.USER_METADATA_FINANCING_RELATIONSHIP_KEY]) {
            relationshipDetails = await this._getRelationshipDetailsService.executeOnce(
                new GetRelationshipDetailsRequest({
                    relationshipKey: metadata[UserMetadata.USER_METADATA_FINANCING_RELATIONSHIP_KEY]
                })
            );
            this._platformIdSubject.next(platformId);
            this._relationshipDetailsSubject.next(relationshipDetails);
            return relationshipDetails;
        } else {
            // This is specifically for the impersonation and DXC flow; we do not get metadata as part of that flow.
            relationshipDetails = await this._getRelationshipDetailsByExternalIdsService.executeOnce(
                new GetRelationshipDetailsByExternalIdsRequest({
                    platformName: platformId,
                    sellerCompanyKey: newUser.currentCompany.companyKey
                })
            );

            this._platformIdSubject.next(platformId);
            this._relationshipDetailsSubject.next(relationshipDetails);

            return relationshipDetails;
        }
    }
}
